import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { DialogTitle } from '@/components/ui/dialog'

interface ShortcutsPanelProps {
  open: boolean
  onClose: () => void
}

/**
 * Keyboard shortcut reference panel — opens with ? key.
 * Shows all registered shortcuts. Purely informational — does not execute actions.
 * Non-wired shortcuts (C, M, W, D) are shown as "coming soon" dimmed items.
 */
export function ShortcutsPanel({ open, onClose }: ShortcutsPanelProps) {
  const { t } = useTranslation('navigation')
  // Close on Escape (CommandDialog handles this natively but being explicit)
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  return (
    <CommandDialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      {/* DialogTitle is required by Radix Dialog for accessibility */}
      <DialogTitle className="sr-only">{t('shortcuts.title')}</DialogTitle>

      <CommandList>
        <CommandEmpty>{t('shortcuts.noShortcuts')}</CommandEmpty>

        <CommandGroup heading="Navigation">
          <ShortcutRow label="Browse offers" keys={['S']} note="Opens search" />
          <ShortcutRow label="Go home" keys={['⌘', 'H']} dimmed />
          <ShortcutRow label="Browse page" keys={['B']} dimmed />
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <ShortcutRow label="Open Pacer" keys={['P']} />
          <ShortcutRow label="Open Pacer" keys={['⌘', 'P']} note="Alt trigger" />
          <ShortcutRow label="Create offer" keys={['C']} dimmed note="coming soon" />
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Account">
          <ShortcutRow label="Account menu" keys={['M']} dimmed note="coming soon" />
          <ShortcutRow label="Drops vault" keys={['D']} dimmed note="coming soon" />
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Chrome">
          <ShortcutRow label="Workspace menu" keys={['W']} dimmed note="coming soon" />
          <ShortcutRow label="Keyboard shortcuts" keys={['?']} />
          <ShortcutRow label="Close / dismiss" keys={['Esc']} />
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}

interface ShortcutRowProps {
  label: string
  keys: string[]
  note?: string
  dimmed?: boolean
}

function ShortcutRow({ label, keys, note, dimmed }: ShortcutRowProps) {
  return (
    <CommandItem
      className={dimmed ? 'opacity-40 cursor-default select-none' : 'cursor-default select-none'}
      // Disable selection/action — this is display-only
      onSelect={() => {}}
    >
      <span className="font-sans text-sm text-slate-700 dark:text-sand-200 flex-1">
        {label}
        {note && (
          <span className="ms-2 text-xs text-slate-400 dark:text-slate-500">— {note}</span>
        )}
      </span>
      <CommandShortcut>
        <span className="flex items-center gap-0.5">
          {keys.map((k, i) => (
            <kbd
              key={i}
              className="inline-flex items-center justify-center rounded border border-sand-300 dark:border-slate-600 bg-sand-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] font-mono font-medium text-slate-600 dark:text-slate-300 min-w-[20px]"
            >
              {k}
            </kbd>
          ))}
        </span>
      </CommandShortcut>
    </CommandItem>
  )
}
